/**
 * Broker property-inspection — admin API client. Drives the full lifecycle:
 * request → quote → mark-paid → schedule → start → report → (badge), or cancel.
 * A PASS report writes a FIELD_INSPECTION verification onto the broker's badge
 * application, which the admin then approves on the badges screen.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';

function getToken(): string {
  return getAuthToken();
}

export type InspectionStatus = 'REQUESTED' | 'QUOTED' | 'PAID' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type InspectionOutcome = 'PASS' | 'FAIL' | 'CONDITIONAL';
export type InspectionBadge = 'VERIFIED_BROKER' | 'GOLD_BROKER';

export interface InspectionAgentLite {
  id: string;
  displayName?: string | null;
  user?: { email?: string | null; phone?: string | null; firstName?: string | null; lastName?: string | null } | null;
}
export interface InspectionPropertyLite {
  id: string;
  titleEn?: string | null;
  titleAr?: string | null;
}

export interface Inspection {
  id: string;
  agentId: string;
  propertyId: string;
  requestedBadgeType: InspectionBadge;
  brokerBadgeId?: string | null;
  status: InspectionStatus;
  feeAmount?: number | null; // halalas (50000 = 500.00 SAR)
  currency: string;
  paymentStatus: string;
  paymentRef?: string | null;
  paidAt?: string | null;
  scheduledAt?: string | null;
  assignedInspectorName?: string | null;
  outcome?: InspectionOutcome | null;
  score?: number | null;
  reportNotes?: string | null;
  reportPhotos?: string | null; // JSON array string
  evaluatedAt?: string | null;
  brokerNote?: string | null;
  cancellationReason?: string | null;
  createdAt: string;
  agent?: InspectionAgentLite | null;
  property?: InspectionPropertyLite | InspectionPropertyDetail | null;
  brokerBadge?: { id: string; status: string; badgeType: string } | null;
}

export interface InspectionPropertyDetail {
  id: string;
  titleEn?: string | null;
  titleAr?: string | null;
  address?: string | null;
  price?: number | null;
}

export interface InspectionListResponse {
  data: Inspection[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export async function fetchInspections(params: { status?: string; badgeType?: string; page?: number; pageSize?: number }): Promise<InspectionListResponse> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.badgeType) q.set('badgeType', params.badgeType);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 20));
  return apiRequest(`/broker-inspections/admin?${q.toString()}`, { token: getToken() });
}

export async function fetchInspection(id: string): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}`, { token: getToken() });
}

export async function quoteInspection(id: string, feeAmount: number, currency = 'SAR'): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/quote`, { method: 'POST', body: { feeAmount, currency }, token: getToken() });
}

export async function markInspectionPaid(id: string, paymentRef?: string): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/mark-paid`, { method: 'POST', body: { paymentRef }, token: getToken() });
}

export async function scheduleInspection(id: string, assignedInspectorName: string, scheduledAt: string): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/schedule`, { method: 'POST', body: { assignedInspectorName, scheduledAt }, token: getToken() });
}

export async function startInspection(id: string): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/start`, { method: 'POST', body: {}, token: getToken() });
}

export async function reportInspection(id: string, payload: { outcome: InspectionOutcome; score?: number; reportNotes?: string; reportPhotos?: string[] }): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/report`, { method: 'POST', body: payload, token: getToken() });
}

export async function cancelInspection(id: string, cancellationReason: string): Promise<{ data: Inspection }> {
  return apiRequest(`/broker-inspections/admin/${id}/cancel`, { method: 'POST', body: { cancellationReason }, token: getToken() });
}

// --- display helpers --------------------------------------------------------

export const BADGE_LABEL: Record<InspectionBadge, string> = {
  VERIFIED_BROKER: 'Trusted Broker (Verified)',
  GOLD_BROKER: 'True Broker (Gold)',
};

export const STATUS_META: Record<InspectionStatus, { label: string; color: string }> = {
  REQUESTED: { label: 'Requested', color: 'bg-blue-100 text-blue-700' },
  QUOTED: { label: 'Quoted', color: 'bg-indigo-100 text-indigo-700' },
  PAID: { label: 'Paid', color: 'bg-cyan-100 text-cyan-700' },
  SCHEDULED: { label: 'Scheduled', color: 'bg-amber-100 text-amber-700' },
  IN_PROGRESS: { label: 'In progress', color: 'bg-purple-100 text-purple-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
};

/** Fee is stored in halalas; show SAR. */
export function feeToSar(feeAmount?: number | null): number {
  return feeAmount ? Math.round(feeAmount) / 100 : 0;
}
